import { useQueries, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { Connection } from 'src/ui/graph/types';
import { checkPermission } from 'src/ui/hooks/db';
import { previousConnectionData } from 'src/ui/hooks/previousConnectionData';

const READ_LOGS_PERMISSION = {
  action: 'READ',
  resourceType: 'dbms-admin',
  resource: 'logs',
} as const;

const readLogsPermissionKeyPrefix = (connectionId: string): unknown[] => [
  'logsPermission',
  connectionId,
];

export const useHasReadLogsPermissions = (
  connection: Connection,
  enabled: boolean
) => {
  const { org = '' } = useParams<{ org?: string }>();
  const queryClient = useQueryClient();
  const keyPrefix = readLogsPermissionKeyPrefix(connection.id);
  const { data: hasLogsPermission } = useQuery(
    [...keyPrefix, connection.token ?? null],
    () => checkPermission(connection, org, READ_LOGS_PERMISSION),
    {
      enabled,
      placeholderData: () =>
        previousConnectionData<boolean>(queryClient, keyPrefix),
    }
  );
  return hasLogsPermission ?? false;
};

export const useHasReadLogsPermissionsForConnections = (
  connections: readonly Connection[]
): Record<string, boolean> => {
  const { org = '' } = useParams<{ org?: string }>();
  const queryClient = useQueryClient();
  const results = useQueries(
    connections.map((c) => {
      const keyPrefix = readLogsPermissionKeyPrefix(c.id);
      return {
        queryFn: () => checkPermission(c, org, READ_LOGS_PERMISSION),
        queryKey: [...keyPrefix, c.token ?? null],
        placeholderData: () =>
          previousConnectionData<boolean>(queryClient, keyPrefix),
      };
    })
  );
  const map: Record<string, boolean> = {};
  results.forEach((r, idx) => {
    map[connections[idx].id] = r.data ?? false;
  });
  return map;
};
